import { VStack } from '@/components/Atoms'
import {
  CheckboxListControl,
  ComboboxSearchControl,
  DateRangeControl,
  FilterControl,
  MonthYearControl,
  MultipleToggleListControl,
  NumberRangeControl,
  SingleToggleListControl,
  sortFiltersByDisplayOrder,
  TextInputControl,
  TriStateCheckboxControl,
  TriStateCheckboxListControl,
} from '@/lib/utils/filters'
import { TriStateValue } from '@/lib/utils/tristate'
import {
  Button,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@repo/ui/components'
import { Table } from '@tanstack/react-table'
import { ListFilterIcon, RefreshCcwIcon, Undo2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { CheckboxList } from './Filters/CheckboxList'
import { ComboboxSearch } from './Filters/ComboboxSearch'
import { DateRange, DateRangeFilter } from './Filters/DateRangeFilter'
import { MonthYearFilter } from './Filters/MonthYearFilter'
import { NumberRange, type NumberRangeFormat } from './Filters/NumberRange'
import MultipleToggleList from './Filters/MultipleToggleList'
import SingleToggleList from './Filters/SingleToggleList'
import TextInput from './Filters/TextInput'
import { TriStateCheckbox } from './Filters/TriStateCheckbox'
import { TriStateCheckboxList, TriStateCheckboxListValues } from './Filters/TriStateCheckboxList'

export type Filter<TValue> = {
  control: FilterControl
  name: string
  title: string
  label?: string
  options: {
    value: string
    label: string
    labelClassName?: string
    /** ComboboxSearch only: extra text the search matches, not shown. */
    keywords?: string[]
  }[]
  triggerText?: string
  searchPlaceholder?: string
  value: TValue
  onChange: React.Dispatch<React.SetStateAction<TValue>>
  shouldHide?: boolean
  loading?: boolean
  /** ComboboxSearch only: scrolls long option labels sideways instead of truncating them. */
  hasHorizontalScroll?: boolean
  /** ComboboxSearch only: renders at most this many matching options; the rest are found by searching. */
  maxVisibleOptions?: number
  /** ComboboxSearch only: splits the search on this character into terms that must all match. */
  searchTermSeparator?: string
  /** ComboboxSearch only: notified as the search box changes, for options fetched as the user types. */
  onSearchChange?: (search: string) => void
  /** ComboboxSearch only: a server search for the current text is still outstanding. */
  isSearching?: boolean
  /** ComboboxSearch only: the list reached its end; append the next page of server matches. */
  onEndReached?: () => void
  /** ComboboxSearch only: the server holds more matches beyond the options given. */
  hasMoreOptions?: boolean
  /** ComboboxSearch only: the next page of matches is being fetched. */
  isLoadingMore?: boolean
}

export type DateRangeFilter<TValue> = Filter<TValue> & {
  control: typeof DateRangeControl
  fromParamName: string
  toParamName: string
}

export type NumberRangeFilter<TValue> = Filter<TValue> & {
  control: typeof NumberRangeControl
  minParamName: string
  maxParamName: string
  /** How the bounds are typed and shown; a whole `count` when absent. */
  format?: NumberRangeFormat
}

type FilterControlsProps<TData, TValue> = {
  table?: Table<TData>
  filters: Filter<TValue>[]
  isOpen?: boolean
  // How many filters are currently applied, shown on the trigger. Comes from the same applied-filters
  // list the badge bar renders, so the two can never disagree.
  activeFilterCount?: number
  onOpenChange?: () => void
  onShowResults?: () => void
  onResetAllFilters?: () => void
}

export function FilterControls<TData, TValue>(props: FilterControlsProps<TData, TValue>) {
  const { t } = useTranslation('common')

  return (
    <Sheet open={props.isOpen} onOpenChange={props.onOpenChange}>
      <SheetTrigger asChild>
        <Button>
          <ListFilterIcon />
          {t('filterControls.triggerText')}
          {!!props.activeFilterCount && (
            <span className="bg-primary-foreground/20 rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums">
              {props.activeFilterCount}
            </span>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent className="w-[512px]">
        <SheetHeader>
          <SheetTitle>{t('filterControls.title')}</SheetTitle>
        </SheetHeader>

        <VStack gap="xl" className="flex-1 overflow-y-auto p-6">
          {sortFiltersByDisplayOrder(props.filters).map((filter, i) => {
            if (filter.shouldHide) return null

            if (filter.control === CheckboxListControl) {
              return (
                <CheckboxList
                  key={i}
                  name={filter.name}
                  title={filter.title}
                  options={filter.options}
                  selectedOptions={filter.value as Set<string>}
                  setSelectedOptions={
                    filter.onChange as React.Dispatch<React.SetStateAction<Set<string>>>
                  }
                />
              )
            }

            if (filter.control === ComboboxSearchControl) {
              return (
                <ComboboxSearch
                  key={i}
                  name={filter.name}
                  triggerText={filter.triggerText}
                  searchPlaceholder={filter.searchPlaceholder}
                  title={filter.title}
                  options={filter.options}
                  selectedOptions={filter.value as string[]}
                  setSelectedOptions={
                    filter.onChange as React.Dispatch<React.SetStateAction<string[]>>
                  }
                  loading={filter.loading}
                  hasHorizontalScroll={filter.hasHorizontalScroll}
                  maxVisibleOptions={filter.maxVisibleOptions}
                  searchTermSeparator={filter.searchTermSeparator}
                  onSearchChange={filter.onSearchChange}
                  isSearching={filter.isSearching}
                  onEndReached={filter.onEndReached}
                  hasMoreOptions={filter.hasMoreOptions}
                  isLoadingMore={filter.isLoadingMore}
                />
              )
            }

            if (filter.control === SingleToggleListControl) {
              return (
                <SingleToggleList
                  key={i}
                  name={filter.name}
                  title={filter.title}
                  options={filter.options}
                  selectedOption={filter.value as string}
                  setSelectedOption={
                    filter.onChange as React.Dispatch<React.SetStateAction<string>>
                  }
                />
              )
            }

            if (filter.control === MultipleToggleListControl) {
              return (
                <MultipleToggleList
                  key={i}
                  name={filter.name}
                  title={filter.title}
                  options={filter.options}
                  selectedOptions={filter.value as string[]}
                  setSelectedOptions={
                    filter.onChange as React.Dispatch<React.SetStateAction<string[]>>
                  }
                />
              )
            }

            if (filter.control === TriStateCheckboxListControl) {
              return (
                <TriStateCheckboxList
                  key={i}
                  name={filter.name}
                  title={filter.title}
                  options={filter.options}
                  values={filter.value as TriStateCheckboxListValues}
                  setValues={
                    filter.onChange as React.Dispatch<
                      React.SetStateAction<TriStateCheckboxListValues>
                    >
                  }
                />
              )
            }

            if (filter.control === TriStateCheckboxControl) {
              return (
                <TriStateCheckbox
                  key={i}
                  name={filter.name}
                  title={filter.title}
                  label={filter.label ?? ''}
                  value={filter.value as TriStateValue}
                  setValue={filter.onChange as React.Dispatch<React.SetStateAction<TriStateValue>>}
                />
              )
            }

            if (filter.control === TextInputControl) {
              return (
                <TextInput
                  key={i}
                  name={filter.name}
                  title={filter.title}
                  placeholder={filter.searchPlaceholder}
                  value={filter.value as string}
                  setValue={filter.onChange as React.Dispatch<React.SetStateAction<string>>}
                />
              )
            }

            if (filter.control === DateRangeControl) {
              return (
                <DateRangeFilter
                  key={i}
                  name={filter.name}
                  title={filter.title}
                  value={filter.value as DateRange}
                  setValue={filter.onChange as React.Dispatch<React.SetStateAction<DateRange>>}
                />
              )
            }

            if (filter.control === MonthYearControl) {
              return (
                <MonthYearFilter
                  key={i}
                  name={filter.name}
                  title={filter.title}
                  value={filter.value as Date | undefined}
                  setValue={
                    filter.onChange as React.Dispatch<React.SetStateAction<Date | undefined>>
                  }
                />
              )
            }

            if (filter.control === NumberRangeControl) {
              return (
                <NumberRange
                  key={i}
                  name={filter.name}
                  title={filter.title}
                  format={(filter as NumberRangeFilter<TValue>).format}
                  value={filter.value as NumberRange}
                  setValue={filter.onChange as React.Dispatch<React.SetStateAction<NumberRange>>}
                />
              )
            }

            return null
          })}
        </VStack>

        <SheetFooter>
          <SheetTrigger asChild>
            <Button variant="secondary" onClick={props.onResetAllFilters}>
              <Undo2Icon />
              {t('filterControls.resetAllFilters')}
            </Button>
          </SheetTrigger>

          <SheetTrigger asChild>
            <Button onClick={props.onShowResults}>
              <RefreshCcwIcon />
              {t('filterControls.showResults')}
            </Button>
          </SheetTrigger>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
